import { useCallback, useEffect, useState } from "react";
import SparketModal from "components/common/modal/modal.tsx";
import { EDIT_PROFILE_PATH, sparketLightGray } from "utils/constants.ts";
import { reportError } from "utils/report-error";
import AmountSelector from "components/commerce/amount-selector.tsx";
import FeeSettingsModal, { FeeMode } from "./fee-settings-modal.tsx";
import { WITHDRAWAL_FEE_MODE } from "utils/constants.ts";
import { BothModePreviewData } from "interfaces/withdrawal-preview.ts";
import WithdrawalSummaryBlock from "./withdrawal-summary-block.tsx";
import SimpleWithdrawalButton from "./simple-withdrawal-button.tsx";
import { toast } from "react-toastify";
import BlockyBoldText from "components/custom-texts/blocky-bold-text.tsx";
import { useAppDispatch, useAppSelector } from "state/hooks.ts";
import { LongButton } from "components/contests/contest-display-items/shared/buttons";
import GreyModalSection from "components/commerce/grey-modal-section.tsx";
import { fetchRealWallet, getRealWalletState } from "state/slices/user-real-wallet-slice.ts";
import { postRequest } from "utils/httpClient.ts";
import { getWithdrawalRequestsPath, getWithdrawalPreviewPath } from "utils/backend-path-builders.ts";
import BalanceDisplay from "components/common/real-money-wallet/balance-display.tsx";
import { getWithdrawFundsModalState, hideWithdrawFundsModal } from "state/slices/withdraw-funds-modal-slice";
import { ErrorResponseBody } from "../../../interfaces/error-response-body";
import { ErrorCodes } from "../../../interfaces/error-codes";
import BankAccountSelector from "components/commerce/v2/bank-account-selector.tsx";
import { hideGlobalLoadingOverlay } from "state/slices/global-loading-overlay-slice";
import { BankAccount } from "interfaces/commerce/bank-account.ts";
import { getBankAccountsState } from "state/slices/bank-accounts-slice.ts";
import PrizeoutButton from "components/bankroll/prizeout-button.tsx";
import { Bank } from "react-bootstrap-icons";
import { NIL_GROUP_ID } from "utils/constants.ts";
import { IDENTITY_VERIFICATION_STATUS } from "interfaces/users-response";
import LoadingOverlay from "components/common/loading-overlay.tsx";
import { useNavigate } from "react-router-dom";
import { getUserState, getVerificationStatus } from "state/slices/user-slice.ts";
import BlockyLightText from "components/custom-texts/blocky-light-text.tsx";
import StyledInteractiveButton from "components/commerce/v2/styled-interactive-button.tsx";
import OrSeparator from "components/commerce/shared/or-separator.tsx";
import { getAppConfig } from "state/slices/app-config-slice.ts";
import { getIntegratedAppState } from "state/slices/integrated-app-slice.ts";
import { getGroupState } from "state/slices/group-slice.ts";
import LightningWithdrawalBlock from "./lightning-withdrawal-block.tsx";

const bankAccountIdField = (account: BankAccount) =>
  account.provider === "PLAID" ? { plaid_account_id: account.id } : { paynote_funding_source_id: account.id };

const WithdrawFundsModal = () => {
  const [processing, setProcessing] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState<number | undefined>(undefined);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<BankAccount>();
  const [showAchDetails, setShowAchDetails] = useState(false);
  const [showFeeSettings, setShowFeeSettings] = useState(false);
  const [feeMode, setFeeMode] = useState<FeeMode>(WITHDRAWAL_FEE_MODE.DEDUCT);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [bothModePreviews, setBothModePreviews] = useState<BothModePreviewData | null>(null);
  
  // Helper function to reset all local state
  const resetModalState = useCallback(() => {
    setSelectedAmount(undefined);
    setSelectedPaymentMethod(undefined);
    setShowAchDetails(false);
    setShowFeeSettings(false);
    setFeeMode(WITHDRAWAL_FEE_MODE.DEDUCT);
    setBothModePreviews(null);
    setPreviewLoading(false);
  }, []);
  
  const verificationStatus = useAppSelector(getVerificationStatus);
  // Derive current preview data from bothModePreviews and current fee mode

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const withdrawModalState = useAppSelector(getWithdrawFundsModalState);
  const realWalletState = useAppSelector(getRealWalletState);
  const userState = useAppSelector(getUserState);
  const appConfig = useAppSelector(getAppConfig);
  const integratedAppState = useAppSelector(getIntegratedAppState);
  const group = useAppSelector(getGroupState);
  const withdrawalProvider = useAppSelector(getBankAccountsState).withdrawalProvider;
  const isSpeedIntegration = group.name === "Speed" && integratedAppState.isIntegratedApp;
  const show = withdrawModalState.show;

  const isBalanceLoading = realWalletState.fetchStatusState === "loading";
  const availableBalance = realWalletState.balance || 0;
  const isIdentityVerified = verificationStatus === IDENTITY_VERIFICATION_STATUS.APPROVED;

  const onClose = useCallback(() => {
    dispatch(hideWithdrawFundsModal());
    dispatch(hideGlobalLoadingOverlay());
    resetModalState();
  }, [dispatch, resetModalState]);

  useEffect(() => {
    if (show) {
      setShowAchDetails(withdrawModalState.autoOpenAchSection);
      dispatch(fetchRealWallet());
    } else {
      // Reset state when modal is hidden
      resetModalState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, withdrawModalState.autoOpenAchSection, resetModalState]);

  // Fetch preview data for both fee modes when amount changes (only if fees are enabled)
  useEffect(() => {
    const fetchBothPreviews = async () => {
      if (selectedAmount && selectedAmount >= 10 && selectedPaymentMethod && appConfig.showWithdrawalFees) {
        setPreviewLoading(true);
        try {
          // Fetch both modes in parallel
          const [deductResponse, addResponse] = await Promise.all([
            postRequest(getWithdrawalPreviewPath(), {
              body: {
                amount: selectedAmount,
                ...bankAccountIdField(selectedPaymentMethod),
                fee_mode: WITHDRAWAL_FEE_MODE.DEDUCT
              }
            }),
            postRequest(getWithdrawalPreviewPath(), {
              body: {
                amount: selectedAmount,
                ...bankAccountIdField(selectedPaymentMethod),
                fee_mode: WITHDRAWAL_FEE_MODE.ADD
              }
            })
          ]);
          
          setBothModePreviews({
            [WITHDRAWAL_FEE_MODE.DEDUCT]: deductResponse,
            [WITHDRAWAL_FEE_MODE.ADD]: addResponse
          });
        } catch (error) {
          console.error('Failed to fetch withdrawal previews:', error);
          // Fall back to client-side calculation
          const feeAmount = selectedAmount * 0.03;
          const fallbackData = {
            [WITHDRAWAL_FEE_MODE.DEDUCT]: {
              fee_amount: feeAmount,
              amount_sent_to_user: selectedAmount - feeAmount,
              amount_deducted_from_balance: selectedAmount
            },
            [WITHDRAWAL_FEE_MODE.ADD]: {
              fee_amount: feeAmount,
              amount_sent_to_user: selectedAmount,
              amount_deducted_from_balance: selectedAmount + feeAmount
            }
          };
          setBothModePreviews(fallbackData);
        } finally {
          setPreviewLoading(false);
        }
      } else {
        setBothModePreviews(null);
        setPreviewLoading(false);
      }
    };

    fetchBothPreviews();
  }, [selectedAmount, selectedPaymentMethod, appConfig.showWithdrawalFees]);

  // Note: previewData is now derived automatically when feeMode or bothModePreviews changes

  const handleWithdraw = async () => {
    if (!selectedPaymentMethod || !selectedAmount) {
      toast.error('Please select a payment method and amount.');
      return;
    }

    if (selectedAmount < 10) {
      toast.error('Minimum withdrawal amount is $10.');
      return;
    }

    setProcessing(true);
    try {
      // Send the original amount and fee mode - backend will calculate everything
      await postRequest(getWithdrawalRequestsPath(), {
        body: {
          amount: selectedAmount,
          ...bankAccountIdField(selectedPaymentMethod),
          fee_mode: feeMode
        }
      });

      toast.success(`Success! Your withdrawal is in process.`);
      dispatch(fetchRealWallet());
      onClose();
    } catch (error: any) {
      const errorResponse: ErrorResponseBody | undefined = error.response?.data;
      if (errorResponse) {
        const error_code = errorResponse.error_code;
        switch (error_code) {
          case ErrorCodes.DepositLimitationExceeded:
            toast.error(errorResponse.friendly_error_message);
            return;
        }
      }
      reportError(error, "withdrawal", { error_code: errorResponse?.error_code });
      toast.error("Something went wrong. Please contact support.");
    } finally {
      setProcessing(false);
      dispatch(hideGlobalLoadingOverlay());
    }
  };

  const handlePrizeoutExit = () => {
    dispatch(fetchRealWallet());
    onClose();
  };

  return (
    <SparketModal title="Withdraw Funds" show={show} onClose={onClose}>
      <div style={{ position: "relative" }}>
        <LoadingOverlay 
          show={processing} 
          message="Processing withdrawal..." 
          zIndex={1050}
        />
        
        <GreyModalSection>
          <BalanceDisplay />
        </GreyModalSection>

        {/* Speed Wallet Mini App: replace Prizeout + ACH with a Lightning
            withdrawal flow that uses the user's p_add Lightning address. */}
        {isSpeedIntegration ? (
          <LightningWithdrawalBlock onClose={onClose} />
        ) : (
          <>
        {/* Prizeout Section - Prominently displayed at top */}
        <GreyModalSection>
          <div className="d-flex flex-column gap-3">
            <div className="text-center" style={{ color: "white" }}>
              <BlockyBoldText>
                <span style={{ 
                  color: "#ffd700", 
                  fontSize: "1.05em", 
                  fontWeight: "800",
                  textTransform: "uppercase"
                }}>
                  Recommended:
                </span>{" "}
                Cash out with popular gift cards and receive up to a 20% bonus!
              </BlockyBoldText>
            </div>
            <div className="d-flex justify-content-center">
              <PrizeoutButton
                onExit={handlePrizeoutExit}
                walletId={realWalletState.walletId || ''}
                groupId={NIL_GROUP_ID}
              />
            </div>
          </div>
        </GreyModalSection>

        <OrSeparator />

        {/* ACH Section */}
        {userState.fetchUserState === "succeeded" && (
          <GreyModalSection>
            <div className="mb-3">
              <StyledInteractiveButton
                onClick={() => {
                  if (!isIdentityVerified) {
                    onClose();
                    navigate(`${EDIT_PROFILE_PATH}?returnTo=/profile&openWithdrawModal=true`);
                  } else {
                    setShowAchDetails(!showAchDetails);
                  }
                }}
                variant="expandable"
                expandIcon={<span>{isIdentityVerified ? (showAchDetails ? '−' : '+') : '→'}</span>}
              >
                <Bank size={16} />
                <BlockyBoldText>
                  Bank Transfer{appConfig.showWithdrawalFees && " • 3% fee"}{!isIdentityVerified && " (ID required)"}
                </BlockyBoldText>
              </StyledInteractiveButton>
            </div>

            {showAchDetails && (
              <>
                {!isIdentityVerified ? (
                  <div className="d-flex flex-column align-items-center p-3">
                    <BlockyBoldText style={{ color: "white", marginBottom: "1rem", textAlign: "center" }}>
                      Identity verification is required for bank transfers
                    </BlockyBoldText>
                    <LongButton
                      text="Verify Identity To Add New Bank Account"
                      onClick={() => navigate(EDIT_PROFILE_PATH)}
                    />
                  </div>
                ) : (
                  <>
                    <div className="mb-3">
                      <AmountSelector
                        presetAmounts={[]}
                        selectedAmount={selectedAmount || 0}
                        setSelectedAmount={(amount) => setSelectedAmount(amount)}
                        placeholder="$10 minimum"
                        disabled={isBalanceLoading}
                        maxAmount={availableBalance}
                      />
                      <div className="mt-2 text-center">
                        <BlockyLightText style={{ color: sparketLightGray, fontSize: "0.85em" }}>
                          Minimum withdrawal amount: $10
                        </BlockyLightText>
                      </div>
                    </div>

                    <div className="mb-3">
                      <BankAccountSelector
                        provider={withdrawalProvider}
                        selectedMethod={selectedPaymentMethod}
                        onMethodSelect={setSelectedPaymentMethod}
                        onClose={onClose}
                        returnUrl="/profile?openWithdrawModal=true"
                      />
                    </div>

                    {/* Conditional Withdrawal UI */}
                    {appConfig.showWithdrawalFees ? (
                      <WithdrawalSummaryBlock
                        amount={selectedAmount}
                        feeMode={feeMode}
                        onSettingsClick={() => setShowFeeSettings(true)}
                        onWithdraw={handleWithdraw}
                        disabled={!selectedPaymentMethod || selectedAmount === undefined || selectedAmount === 0 || selectedAmount < 10}
                        processing={processing}
                        summaryDisabled={selectedAmount === undefined || selectedAmount < 10}
                        previewData={bothModePreviews ? bothModePreviews[feeMode] : null}
                        previewLoading={previewLoading}
                      />
                    ) : (
                      <SimpleWithdrawalButton
                        onWithdraw={handleWithdraw}
                        disabled={!selectedPaymentMethod || selectedAmount === undefined || selectedAmount === 0 || selectedAmount < 10 || processing}
                        processing={processing}
                      />
                    )}

                  </>
                )}
              </>
            )}
          </GreyModalSection>
        )}
          </>
        )}
      </div>

      {/* Fee Settings Modal - Bottom Sheet */}
      {appConfig.showWithdrawalFees && (
        <FeeSettingsModal
          show={showFeeSettings}
          onClose={() => setShowFeeSettings(false)}
          feeMode={feeMode}
          onFeeModeChange={setFeeMode}
          withdrawAmount={selectedAmount || 0}
          previewData={bothModePreviews}
        />
      )}
    </SparketModal>
  );
};

export default WithdrawFundsModal; 